import { MultiSelect, SegmentedControl } from '@mantine/core';
import { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import styles from './EventGlobalScopeInput.module.scss';

import { EventKind, EventScope } from 'api/dtos/event';
import useGetCurrentEntityId from 'hooks/useGetCurrentEntityId';
import useGetUser from 'hooks/useGetUser';
import { useEventContext } from 'Screens/Dashboard/Events/EventHandler/EntityEventContext';

const EventGlobalScopeInput = () => {
    const intl = useIntl();
    const user = useGetUser();
    const currentEntityId = useGetCurrentEntityId();
    const { values, updateValues } = useEventContext();
    const selectedEntityId = values.entityId ?? currentEntityId;
    const selectedEntity = (user?.entities ?? []).find((entity) => entity.id === selectedEntityId);

    const childGroups = useMemo(() => (user?.entities ?? [])
        .filter((entity) => entity.parentId === selectedEntityId && !entity.isIndependent)
        .map((entity) => ({
            value: entity.id,
            label: entity.name,
        })), [selectedEntityId, user?.entities]);

    const canManageGlobalEvents = Boolean(
        selectedEntity?.isParish &&
        !selectedEntity.parentId &&
        !selectedEntity.isIndependent,
    );
    const isCreate = !values.isEdit && !values.id;
    const canChooseKind = canManageGlobalEvents && isCreate;
    const canEditGlobalScope = canManageGlobalEvents && values.isGlobalSource;
    const isGlobal = values.kind === 'Global' || values.isGlobal || values.isGlobalSource;

    if (!canChooseKind && !canEditGlobalScope) {
        return null;
    }

    const updateKind = (kind: string) => {
        const nextKind = kind as EventKind;
        updateValues({
            kind: nextKind,
            scope: nextKind === 'Global' ? 'GlobalAllChildren' : 'LocalOnly',
            targetEntityIds: [],
        });
    };

    const updateScope = (scope: string) => {
        const nextScope = scope as EventScope;
        updateValues({
            kind: 'Global',
            scope: nextScope,
            targetEntityIds: nextScope === 'GlobalSelectedChildren' ? values.targetEntityIds ?? [] : [],
        });
    };

    return (
        <div className={styles.container}>
            {canChooseKind && (
                <div className={styles.segmentedField}>
                    <div className={styles.fieldLabel}>{intl.formatMessage(messages.kindLabel)}</div>
                    <SegmentedControl
                        fullWidth
                        value={values.kind ?? 'Local'}
                        onChange={updateKind}
                        classNames={{
                            root: styles.buttonSegmentedRoot,
                            control: styles.buttonSegmentedControl,
                            input: styles.buttonSegmentedInput,
                            label: styles.buttonSegmentedLabel,
                            indicator: styles.buttonSegmentedIndicator,
                        }}
                        data={[
                            { value: 'Local', label: intl.formatMessage(messages.localEvent) },
                            { value: 'Global', label: intl.formatMessage(messages.globalEvent) },
                        ]}
                    />
                </div>
            )}
            {isGlobal && (
                <>
                    <div className={styles.segmentedField}>
                        <div className={styles.fieldLabel}>{intl.formatMessage(messages.scopeLabel)}</div>
                        <SegmentedControl
                            fullWidth
                            value={values.scope === 'GlobalSelectedChildren' ? 'GlobalSelectedChildren' : 'GlobalAllChildren'}
                            onChange={updateScope}
                            classNames={{
                                root: styles.buttonSegmentedRoot,
                                control: styles.buttonSegmentedControl,
                                input: styles.buttonSegmentedInput,
                                label: styles.buttonSegmentedLabel,
                                indicator: styles.buttonSegmentedIndicator,
                            }}
                            data={[
                                { value: 'GlobalAllChildren', label: intl.formatMessage(messages.allChildren) },
                                { value: 'GlobalSelectedChildren', label: intl.formatMessage(messages.selectedChildren) },
                            ]}
                        />
                    </div>
                    {values.scope === 'GlobalSelectedChildren' && (
                        <MultiSelect
                            id="event-global-targets-picker"
                            label={intl.formatMessage(messages.childrenLabel)}
                            placeholder={intl.formatMessage(messages.childrenPlaceholder)}
                            data={childGroups}
                            value={values.targetEntityIds ?? []}
                            onChange={(targetEntityIds) => { updateValues({ targetEntityIds }); }}
                            error={(values.targetEntityIds ?? []).length === 0
                                ? intl.formatMessage(messages.childrenRequired)
                                : undefined}
                            radius="md"
                            size="md"
                            searchable
                            clearable
                            comboboxProps={{ withinPortal: false }}
                        />
                    )}
                    {childGroups.length === 0 && (
                        <p className={styles.helperText}>{intl.formatMessage(messages.noChildren)}</p>
                    )}
                </>
            )}
        </div>
    );
};

const messages = defineMessages({
    kindLabel: {
        id: 'src.Screens.Dashboard.Events.EventHandler.inputs.EventGlobalScopeInput.kindLabel',
        defaultMessage: 'Typ wydarzenia',
    },
    localEvent: {
        id: 'src.Screens.Dashboard.Events.EventHandler.inputs.EventGlobalScopeInput.localEvent',
        defaultMessage: 'Lokalne',
    },
    globalEvent: {
        id: 'src.Screens.Dashboard.Events.EventHandler.inputs.EventGlobalScopeInput.globalEvent',
        defaultMessage: 'Globalne',
    },
    scopeLabel: {
        id: 'src.Screens.Dashboard.Events.EventHandler.inputs.EventGlobalScopeInput.scopeLabel',
        defaultMessage: 'Zakres globalny',
    },
    allChildren: {
        id: 'src.Screens.Dashboard.Events.EventHandler.inputs.EventGlobalScopeInput.allChildren',
        defaultMessage: 'Wszystkie podgrupy',
    },
    selectedChildren: {
        id: 'src.Screens.Dashboard.Events.EventHandler.inputs.EventGlobalScopeInput.selectedChildren',
        defaultMessage: 'Wybrane podgrupy',
    },
    childrenLabel: {
        id: 'src.Screens.Dashboard.Events.EventHandler.inputs.EventGlobalScopeInput.childrenLabel',
        defaultMessage: 'Podgrupy',
    },
    childrenPlaceholder: {
        id: 'src.Screens.Dashboard.Events.EventHandler.inputs.EventGlobalScopeInput.childrenPlaceholder',
        defaultMessage: 'Wybierz podgrupy',
    },
    childrenRequired: {
        id: 'src.Screens.Dashboard.Events.EventHandler.inputs.EventGlobalScopeInput.childrenRequired',
        defaultMessage: 'Wybierz co najmniej jedna podgrupe',
    },
    noChildren: {
        id: 'src.Screens.Dashboard.Events.EventHandler.inputs.EventGlobalScopeInput.noChildren',
        defaultMessage: 'Brak podgrup dostepnych dla tej grupy.',
    },
});

export default EventGlobalScopeInput;
